<script setup lang="ts">
    import { computed, ref, watch, provide } from 'vue';
    import { RouterView, useRoute } from 'vue-router';
    import ContactList from '@views/Lists/ContactList.vue';
    import type { ContactsResponse } from '@views/Lists/ContactList.access';
    import { loadContact } from '@views/Contact/Contact.access';
    import type { Contact } from '@views/Contact/ContactTypes';
    import {
        buildQueryString,
        getRespondents,
        getIssueRespondents,
        getIssueNonRespondents,
        getNonRespondents,
        getQuestionOptionRespondents,
        getQuestionRespondents,
        getNpsGroupRespondents,
        exportRespondentsExcel,
        exportRespondentsCsv,
        exportRespondentsToNewList,
        exportRespondentsToExistingList,
        requestRespondentsExcelByMail,
        type DateRange,
        type NpsGroup,
        type RespondentsEndpointOptions,
    } from '../SurveyStatistics.access';
    import type { QuestionFilter } from '../../SurveysTypes';

    const props = defineProps<{
        surveyId: string;
        dateRange: DateRange;
        questionFilters: QuestionFilter[];
        autoFilters: QuestionFilter[];
        selectedCultures: string[];
    }>();

    const emit = defineEmits<{
        toggleAnswerFilter: [value: string];
    }>();

    const route = useRoute();

    const contactListRef = ref<InstanceType<typeof ContactList> | null>(null);
    const lastViewedContactId = ref<string | null>(null);
    const contactWasUpdated = ref(false);

    provide('markContactUpdated', () => {
        contactWasUpdated.value = true;
    });

    watch(
        () => route.params.contactId as string | undefined,
        (id) => {
            if (id) {
                lastViewedContactId.value = id;
                contactWasUpdated.value = false;
            }
        }
    );

    const showingContact = computed(() => route.name === 'SurveyStatisticsRespondentContact');

    watch(showingContact, async (showing, wasShowing) => {
        if (wasShowing && !showing && lastViewedContactId.value && contactWasUpdated.value) {
            const updated = await loadContact(lastViewedContactId.value);
            contactListRef.value?.updateContact(updated);
        }
    });
    const isNonRespondents = computed(() => route.query.mode === 'nonrespondents');
    const issueId = computed(() => (typeof route.query.issueId === 'string' ? route.query.issueId : null));
    const issueSubject = computed(() => (typeof route.query.issueSubject === 'string' ? route.query.issueSubject : ''));
    const questionOptionId = computed(() => (typeof route.query.questionOptionId === 'string' ? route.query.questionOptionId : null));
    const questionId = computed(() => (typeof route.query.questionId === 'string' ? route.query.questionId : null));

    function isAnswerInAutoFilter(value: string): boolean {
        return !!questionId.value && props.autoFilters.some((f) => f.QuestionId === questionId.value && f.Value === value);
    }

    function isAnswerInFilter(value: string): boolean {
        return !!questionId.value && props.questionFilters.some((f) => f.QuestionId === questionId.value && f.Value === value);
    }
    const culture = computed(() => (typeof route.query.culture === 'string' ? route.query.culture : null));
    const npsGroup = computed(() => (typeof route.query.npsGroup === 'string' ? (route.query.npsGroup as NpsGroup) : null));
    const npsGroupQuestionId = computed(() => (typeof route.query.npsGroupQuestionId === 'string' ? route.query.npsGroupQuestionId : null));

    const title = computed(() => {
        if (npsGroup.value) return $Resources[`Nps${npsGroup.value}Heading`];
        if (questionOptionId.value) return $Resources.StatisticsSurveyQuestionOptionRespondentsHeading;
        if (questionId.value) return $Resources.StatisticsSurveyQuestionRespondentsHeading;
        if (issueId.value) {
            return isNonRespondents.value
                ? $Resources.StatisticsSurveyNonRespondentsFromIssueHeading.replace('{{Issue}}', issueSubject.value)
                : $Resources.StatisticsSurveyRespondentsFromIssueHeading.replace('{{Issue}}', issueSubject.value);
        }
        return isNonRespondents.value ? $Resources.StatisticsSurveyNonRespondentsHeading : $Resources.StatisticsSurveyRespondentsHeading;
    });

    const description = computed(() => {
        if (npsGroup.value) return $Resources[`Nps${npsGroup.value}Introduction`];
        if (questionOptionId.value) return $Resources.StatisticsSurveyQuestionOptionRespondentsIntroduction;
        if (questionId.value) return $Resources.StatisticsSurveyQuestionRespondentsIntroduction;
        if (issueId.value) {
            return isNonRespondents.value
                ? $Resources.StatisticsSurveyNonRespondentsFromIssueBody.replace('{{Issue}}', issueSubject.value)
                : $Resources.StatisticsSurveyRespondentsFromIssueBody.replace('{{Issue}}', issueSubject.value);
        }
        return isNonRespondents.value ? $Resources.StatisticsSurveyNonRespondentsIntroduction : $Resources.StatisticsSurveyRespondentsIntroduction;
    });

    function buildQs(): string {
        return buildQueryString(props.dateRange, props.questionFilters, culture.value ? [culture.value] : props.selectedCultures);
    }

    async function fetchContacts(skip: number, pageSize: number, extra: string): Promise<ContactsResponse> {
        const qs = buildQs();
        const result = questionOptionId.value
            ? await getQuestionOptionRespondents(props.surveyId, questionOptionId.value, qs, skip, pageSize, extra)
            : questionId.value
              ? await getQuestionRespondents(props.surveyId, questionId.value, qs, skip, pageSize, extra)
              : npsGroup.value && npsGroupQuestionId.value
              ? await getNpsGroupRespondents(props.surveyId, npsGroupQuestionId.value, npsGroup.value, qs, skip, pageSize, extra)
              : issueId.value
                ? isNonRespondents.value
                    ? await getIssueNonRespondents(props.surveyId, issueId.value, qs, skip, pageSize, extra)
                    : await getIssueRespondents(props.surveyId, issueId.value, qs, skip, pageSize, extra)
                : isNonRespondents.value
                  ? await getNonRespondents(props.surveyId, qs, skip, pageSize, extra)
                  : await getRespondents(props.surveyId, qs, skip, pageSize, extra);
        return result as unknown as ContactsResponse;
    }

    function respondentsEndpointOptions(): RespondentsEndpointOptions {
        return {
            issueId: issueId.value,
            isNonRespondents: isNonRespondents.value,
            questionOptionId: questionOptionId.value,
            questionId: questionId.value,
            npsGroup: npsGroup.value,
            npsGroupQuestionId: npsGroupQuestionId.value,
        };
    }

    async function exportExcel(extra: string): Promise<{ Url: string }> {
        return exportRespondentsExcel(props.surveyId, buildQs(), extra, respondentsEndpointOptions());
    }

    async function exportCsv(extra: string): Promise<{ Url: string }> {
        return exportRespondentsCsv(props.surveyId, buildQs(), extra, respondentsEndpointOptions());
    }

    async function exportToNewList(extra: string): Promise<{ ListId: string }> {
        return exportRespondentsToNewList(props.surveyId, buildQs(), extra, respondentsEndpointOptions());
    }

    async function exportToExistingList(extra: string, targetListId: string): Promise<{ ListId: string }> {
        return exportRespondentsToExistingList(props.surveyId, buildQs(), extra, respondentsEndpointOptions(), targetListId);
    }

    async function sendExcelByMail(extra: string): Promise<void> {
        return requestRespondentsExcelByMail(props.surveyId, buildQs(), extra, respondentsEndpointOptions());
    }

    function contactRoute(contactId: string) {
        return { name: 'SurveyStatisticsRespondentContact', params: { surveyId: props.surveyId, contactId }, query: { ...route.query } };
    }

    const contactListKey = computed(
        () =>
            `${route.query.mode ?? ''}-${issueId.value ?? ''}-${questionOptionId.value ?? ''}-${questionId.value ?? ''}-${npsGroup.value ?? ''}-${culture.value ?? ''}-${props.dateRange?.start}-${props.dateRange?.end}-${JSON.stringify(props.questionFilters)}-${JSON.stringify(props.selectedCultures)}`
    );

    function responseCountRoute(contact: Contact) {
        return {
            name: 'SurveyStatisticsResponsesForContact',
            params: { surveyId: props.surveyId, contactId: contact.ContactId },
            query: { ...route.query },
        };
    }

    const extraCountFields = [{ key: 'ResponseCount' as const, label: $Resources.ContactResponseCountLabel, route: responseCountRoute }];
</script>

<template>
    <RouterView v-if="showingContact" />

    <div v-show="!showingContact">
        <ContactList
            ref="contactListRef"
            :key="contactListKey"
            :title="title"
            :description="description"
            :fetchContacts="fetchContacts"
            :contactRoute="contactRoute"
            :extraCountFields="extraCountFields"
            :exportExcel="exportExcel"
            :exportCsv="exportCsv"
            :exportToNewList="exportToNewList"
            :exportToExistingList="exportToExistingList"
            :sendExcelByMail="sendExcelByMail"
            :isAnswerInAutoFilter="isAnswerInAutoFilter"
            :isAnswerInFilter="isAnswerInFilter"
            :scalarOnly="true"
            @toggleAnswerFilter="(value) => emit('toggleAnswerFilter', value)" />
    </div>
</template>

